/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { formatNumber } from 'cio/helpers/format-number';
import pluralize from 'cio/utils/pluralize';

import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import AlertsService from 'cio/services/alerts';
import { joinWordsAsSnippets } from 'cio/utils/join-words';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';

interface PeopleDeleteConfirmModalArgs {
  deletedPeopleCount: number;
  isBulkDeletion: boolean;
  isDisabled: boolean;
  onClose: () => unknown;
  confirmAction: (args: { suppress: boolean }) => unknown;
}

const DEFAULT_VALUES = {
  confirmationString: '',
  suppressDeletedPeople: false,
};

export default class PeopleDeleteConfirmModal extends Component<PeopleDeleteConfirmModalArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service alerts!: AlertsService;

  @tracked confirmationString = DEFAULT_VALUES.confirmationString;
  @tracked suppressDeletedPeople = DEFAULT_VALUES.suppressDeletedPeople;

  get titleText() {
    return this.args.deletedPeopleCount > 1
      ? `Delete ${formatNumber([this.args.deletedPeopleCount])} people forever`
      : 'Delete forever';
  }

  get validConfirmationString() {
    return formatNumber([this.args.deletedPeopleCount]);
  }

  get confirmPeopleDeleteCount() {
    return !(
      this.confirmationString === this.validConfirmationString ||
      this.confirmationString === this.args.deletedPeopleCount.toString()
    );
  }

  get isConfirmSubmitDisabled() {
    return (
      this.confirmTask.isRunning || this.confirmPeopleDeleteCount || !this.auth.hasPermission('journeys/people/delete')
    );
  }

  get identifierDescriptions() {
    return joinWordsAsSnippets(this.environment.current.enabledIdentifiers, ', ', ' or ');
  }

  closeAndResetState() {
    this.confirmationString = DEFAULT_VALUES.confirmationString;
    this.suppressDeletedPeople = DEFAULT_VALUES.suppressDeletedPeople;
    this.args.onClose();
  }

  confirmTask = task(async () => {
    try {
      await this.args.confirmAction({ suppress: this.suppressDeletedPeople });
      let pluralizePeopleString = `${formatNumberAndPluralize([this.args.deletedPeopleCount, 'person'])}`;
      let alertText = this.suppressDeletedPeople
        ? `${pluralizePeopleString} will be deleted and suppressed soon`
        : `${pluralizePeopleString} will be deleted soon`;
      this.alerts.success('people.delete', alertText);
      this.closeAndResetState();
    } catch (e) {
      this.alerts.error(
        'people.delete.modal',
        `Something went wrong with deleting ${pluralize(
          this.args.deletedPeopleCount,
          'this person',
          'these people',
        )}. Please try again or contact support.`,
      );
    }
  });

  @action
  closeModal() {
    if (!this.confirmTask.isRunning) {
      this.closeAndResetState();
    }
  }
}
